import React, { useContext } from 'react';
import styled from 'styled-components';
import './Textarea.scss';
import { DataElementContext } from '../common/DataElementContext';
import { equalWithoutChildren } from '../../utils/fastDeepEqual';
import { processComponentProps } from '@/page-components/utils/processComponentProps';
import { useTranslation } from 'react-i18next';

const defaultProps = {
  className: '',
  styleText: '',
  properties: {
    inputType: 'text',
    placeholder: '',
    autoFocus: false,
  },
  visibility: {},
};

const StyledTextarea = styled.textarea((props) => props.$styleText);

const Textarea = (componentProps) => {
  let props = componentProps;
  const ref = React.useRef(null);
  const { i18n, t } = useTranslation();

  const dataElementContext = useContext(DataElementContext);
  let isVisible = true;

  [props, isVisible] = processComponentProps(props, dataElementContext);

  const autoGrowEnabled = props.properties.maxRows != null;

  // Keep latest props accessible inside effects without adding them as deps
  const propsRef = React.useRef(props);
  propsRef.current = props;

  React.useEffect(() => {
    if (props.properties.autoFocus && ref.current !== null) {
      ref.current.focus();
    }
  }, [props.properties.autoFocus]);

  React.useEffect(() => {
    if (!ref.current || !autoGrowEnabled) return;
    const el = ref.current;
    const { value, maxRows } = propsRef.current.properties;
    if (!value) {
      el.style.height = '';
      el.style.overflowY = '';
      return;
    }
    el.style.height = '0';
    const scrollH = el.scrollHeight;
    const cs = getComputedStyle(el);
    let lineHeight = parseFloat(cs.lineHeight);
    if (isNaN(lineHeight)) lineHeight = parseFloat(cs.fontSize) * 1.2;
    const paddingTop = parseFloat(cs.paddingTop);
    const paddingBottom = parseFloat(cs.paddingBottom);
    const maxH = maxRows * lineHeight + paddingTop + paddingBottom;
    el.style.height = `${Math.min(scrollH, maxH)}px`;
    el.style.overflowY = scrollH > maxH ? 'auto' : 'hidden';
  }, [props.properties.value, autoGrowEnabled]);

  if (!isVisible) return null;

  const handleAutoGrow = (e) => {
    if (!autoGrowEnabled) {
      if (propsRef.current.properties.onInput) propsRef.current.properties.onInput(e);
      return;
    }
    const el = e.target;
    const { maxRows } = propsRef.current.properties;
    el.style.height = '0';
    const scrollH = el.scrollHeight;
    const cs = getComputedStyle(el);
    let lineHeight = parseFloat(cs.lineHeight);
    if (isNaN(lineHeight)) lineHeight = parseFloat(cs.fontSize) * 1.2;
    const paddingTop = parseFloat(cs.paddingTop);
    const paddingBottom = parseFloat(cs.paddingBottom);
    const maxH = maxRows * lineHeight + paddingTop + paddingBottom;
    el.style.height = `${Math.min(scrollH, maxH)}px`;
    el.style.overflowY = scrollH > maxH ? 'auto' : 'hidden';
    if (propsRef.current.properties.onInput) propsRef.current.properties.onInput(e);
  };

  const allInputProps = {};
  if (props?.properties) {
    Object.keys(props?.properties).forEach((key) => {
      if (key.indexOf('data-') === 0) {
        allInputProps[key] = props?.properties[key];
      }
    });
  }

  if (props.className != null) allInputProps.className = props.className;
  if (props.properties.disabled != null) allInputProps.disabled = props.properties.disabled;
  if (props.properties.name != null) allInputProps.name = props.properties.name;
  if (props.properties.value != null && props.properties.onChange != null) allInputProps.value = props.properties.value;
  if (props.properties.required != null) allInputProps.required = props.properties.required;
  if (props.properties.onChange != null) allInputProps.onChange = props.properties.onChange;
  if (props.properties.onKeyUp != null) allInputProps.onKeyUp = props.properties.onKeyUp;
  if (props.properties.onKeyDown != null) allInputProps.onKeyDown = props.properties.onKeyDown;
  if (props.properties.onFocus != null) allInputProps.onFocus = props.properties.onFocus;
  if (props.properties.onBlur != null) allInputProps.onBlur = props.properties.onBlur;
  if (props.properties.placeholder != null) allInputProps.placeholder = t(props.properties.placeholder);
  if (props.properties.autoFocus != null) allInputProps.autoFocus = props.properties.autoFocus;
  if (props.properties.checked != null) allInputProps.checked = props.properties.checked;
  if (props.properties.defaultChecked != null) allInputProps.defaultChecked = props.properties.defaultChecked;
  if (props.properties.min != null) allInputProps.min = props.properties.min;
  if (props.properties.max != null) allInputProps.max = props.properties.max;
  if (props.properties.pattern != null) allInputProps.pattern = props.properties.pattern;
  if (props.properties.rows != null) allInputProps.rows = props.properties.rows;
  else allInputProps.rows = 1;
  if (props.properties.cols != null) allInputProps.cols = props.properties.cols;

  return (
    <StyledTextarea
      {...allInputProps}
      onInput={handleAutoGrow}
      ref={ref}
      data-node-id={props.nodeId}
      data-is-node="true"
      $styleText={`resize:none;box-sizing:border-box;${props.styleText}`}
      style={props.style}
    />
  );
};

export default React.memo(Textarea, equalWithoutChildren);
